import {
  CustomDataGrid,
  Loading,
  MButton,
  MCheckbox,
  ResponsiveFormContainer,
} from "@app/components";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { IsotopeResultCalculationResponseTypes } from "@app/types/samples.types";
import {
  Box,
  Grid,
  List,
  ListItem,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { lastDetector, lastEquipment, styleModal } from "../IG/helpers";
import { styles } from "../../styles";
import { toExponentialUppercase } from "@app/utils/functions";
import { ResultDataTable } from "../commonForms/calculation";
import { useEquipment } from "@services/servicesGenericsTables";

const OutDataGT = () => {
  const { getValues, setValue, watch } = useFormContext();
  const {
    fetchIsotopeResultCalculationByID,
    putResult,
    fetchCalculation,
    calculateDataOutGT,
    loadingDateAxe,
  } = useAnalysis();
  const { equipmentList } = useEquipment();
  const [open, setOpen] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const typeAnalysisName = getValues("typeAnalysis")?.descripcion;
  const [results, setResults] = useState<ResultDataTable[]>([]);
  const [resultsAll, setResultsAll] = useState<any[]>([]);
  const watchCountingEquipment = watch("counting_equipment");
  const watchSpectrum = watch("cps_spectrum_area_shows");
  const watchAbsorptionFactor = watch("calculation_self_absorption_factor");
  const watchGeometricFactor = watch("calculation_geometric_factor");
  const watchPerformance = watch("calculation_performance");
  const watchAmountSample = watch("calculation_amount_sample");
  const watchIntegralSpectrumSampleAccounts = watch(
    "integral_spectrum_sample_accounts"
  );
  const watchTotalSampleCountingTime = watch(
    "total_sample_counting_time"
  );

  useEffect(() => {
    const fetchData = async () => {
      const calculationEquipment = lastEquipment(
        getValues("calculation_equipment"),
        equipmentList
      );

      const response = await calculateDataOutGT(
        calculationEquipment?.id || 0,
        watchSpectrum,
        watchAbsorptionFactor,
        watchGeometricFactor,
        watchPerformance,
        watchAmountSample,
        watchIntegralSpectrumSampleAccounts,
        watchTotalSampleCountingTime
      );

      setResults(response?.result);
      setResultsAll(response?.outData);
    };
    fetchData();
  }, [
    watchCountingEquipment,
    watchSpectrum,
    watchAbsorptionFactor,
    watchGeometricFactor,
    watchPerformance,
    watchAmountSample,
    watchIntegralSpectrumSampleAccounts,
    watchTotalSampleCountingTime,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const preloadResults = async () => {
      const idResultPreload = getValues("idResultPreload");
      if (idResultPreload !== undefined) {
        const response = await fetchCalculation(idResultPreload);
        if (response) {
          setValue("to_report", response.to_reports ? "to_report" : "");
        }
      }
    };
    preloadResults();
  }, [getValues("idResultPreload")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToReportUpdate = () => {
    if (getValues("idResultPreload")) {
      putResult(getValues("idResultPreload"), true);
      setValue("to_report", "to_report");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = async () => {
    setOpen(true);
    await fetchIsotopeResultCalculationByID();
  };

  return (
    <>
      {getValues("idResultPreload") && (
        <>
          {!loadingDateAxe && results?.length > 0 ? (
            <>
              <Grid
                container
                spacing={2}
                py={4}
                px={1}
                justifyContent="center"
                alignItems="end"
              >
                <Grid item xs={12} md={12} className="flexLeft">
                  <Typography variant="h6" sx={styles.resultsTableHeader}>
                    RESULTADOS:
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}></Grid>
                <Grid item xs={12} md={6} className="flexCenter">
                  <TableContainer component={Paper}>
                    <Table sx={styles.resultsTable}>
                      <TableBody>
                        {results.map((result, index) => (
                          <TableRow key={index}>
                            <TableCell
                              sx={{
                                ...styles.resultsTableCell,
                                ...styles.resultsTableCellLabel,
                              }}
                            >
                              {result.label}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...styles.resultsTableCell,
                                ...styles.resultsTableCellValue,
                              }}
                            >
                              {toExponentialUppercase(Number(result.value), 4)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </>
          ) : null}
          <ResponsiveFormContainer columns={1} ml={0.5}>
            <Box display="flex" justifyContent="center" mt={2}>
              <MCheckbox
                key={JSON.stringify(watch("to_report"))}
                name="to_report"
                options={[
                  {
                    label: "Para informe",
                    value: "to_report",
                  },
                ]}
                multiple={false}
                disabled={true}
              />
              <MButton
                variant="contained"
                color="primary"
                text="Utilizar resultado en informe"
                onClick={handleToReportUpdate}
                disabled={getValues("to_report") === "to_report"}
              />
            </Box>
          </ResponsiveFormContainer>
          <Grid
            item
            xs={12}
            md={12}
            display="flex"
            justifyContent="flex-end"
            gap={2}
            mt={2}
          >
            <MButton
              variant="outlined"
              color="secondary"
              text=" Datos de salida"
              onClick={() => handleOpen()}
            />
          </Grid>

          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <>
              {loadingDateAxe && <Loading />}
              <Box sx={styleModal} ref={modalContentRef}>
                <Typography
                  id="modal-title-IG"
                  variant="h6"
                  component="h2"
                  sx={{ mb: 2, borderBottom: 3, borderColor: "divider" }}
                >
                  Datos de salida {typeAnalysisName && ` - ${typeAnalysisName}`}
                </Typography>
                <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                  {Array.isArray(resultsAll) && resultsAll.length > 0 ? (
                    resultsAll.map((item, index) => (
                      <ListItem key={index} disableGutters sx={{ p: 0 }}>
                        <Grid
                          container
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{
                            mb: 0,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Grid item xs={8} sm={9} md={10}>
                            <Typography
                              noWrap
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={3} md={2}>
                            <Typography sx={{ textAlign: "right" }}>
                              {item.value}
                            </Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                    ))
                  ) : (
                    <Typography sx={{ textAlign: "center", mt: 2 }}>
                      No hay datos disponibles
                    </Typography>
                  )}
                </List>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 2,
                    gap: 2,
                  }}
                >
                  <MButton
                    onClick={handleClose}
                    text="Cerrar"
                    variant="contained"
                  />
                </Box>
              </Box>
            </>
          </Modal>
        </>
      )}
    </>
  );
};

export default OutDataGT;
